/**
 * Panel (27/10, Gabor): todo de un vistazo, por bloques, y cada tarjeta lleva a su lista.
 * Datos: `admin-dashboard` (una llamada; contadores por agregación e ingresos convertidos a EUR).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@nuvvu/shared/api';
import type { AdminDashboardOutput, AdminDashboardRevenue } from '@nuvvu/shared/api';
import { tokens } from '@nuvvu/ui';
import { call, errorText } from '../api.js';
import { btn, card, money, muted, Notice } from '../lib/ui.js';

const eur = (cents: number): string => money({ amount: cents, currency: 'EUR' });
const TIER_NAME: Record<string, string> = {
  elite: 'PRO Absoluto',
  business: 'PRO Max',
  plus: 'PRO Plus',
  basic: 'PRO',
};

interface Tile {
  label: string;
  value: string | number;
  to?: string;
  sub?: string;
  /** Rojo si > 0 (algo espera al equipo). */
  alert?: boolean;
}

function Tiles({ title, tiles }: { title: string; tiles: Tile[] }): ReactElement {
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <h2 style={{ margin: '8px 0 0', fontSize: 15, color: tokens.color.muted }}>{title}</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 10,
        }}
      >
        {tiles.map((t) => {
          const hot = t.alert === true && Number(t.value) > 0;
          const body = (
            <>
              <span style={muted}>{t.label}</span>
              <strong style={{ fontSize: 26, color: hot ? tokens.color.danger : tokens.color.fg }}>
                {t.value}
              </strong>
              {t.sub && <span style={{ ...muted, fontSize: 12 }}>{t.sub}</span>}
            </>
          );
          const style = {
            ...card,
            gap: 4,
            textDecoration: 'none',
            color: 'inherit',
            borderColor: hot ? tokens.color.danger : tokens.color.border,
          };
          return t.to ? (
            <Link key={t.label} to={t.to} style={style}>
              {body}
            </Link>
          ) : (
            <section key={t.label} style={style}>
              {body}
            </section>
          );
        })}
      </div>
    </section>
  );
}

function revenueTiles(r: AdminDashboardRevenue): Tile[] {
  return [
    { label: 'Ingresos nuvvu', value: eur(r.total), to: '/ingresos', sub: 'sin PRO (va aparte)' },
    { label: 'Ventas completadas', value: r.sales, to: '/pedidos?status=completed' },
    { label: 'Volumen vendido', value: eur(r.gmv), to: '/ingresos' },
    { label: 'Protección (seguro)', value: eur(r.protection), to: '/ingresos' },
    {
      label: 'Transporte',
      value: eur(r.transport - r.campaign_subsidies),
      to: '/ingresos',
      sub:
        r.campaign_subsidies > 0
          ? `${eur(r.transport)} − ${eur(r.campaign_subsidies)} de campañas`
          : 'recargo de transporte',
    },
    {
      label: 'Destacados y subidas',
      value: eur(r.promotions),
      to: '/monetizacion',
      sub: `${String(r.promotions_count)} pagados`,
    },
    { label: 'Retiradas instantáneas', value: eur(r.withdrawal_fees), to: '/retiradas' },
  ];
}

export function DashboardPage(): ReactElement {
  const [days, setDays] = useState(30);
  const [d, setD] = useState<AdminDashboardOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    call(api.admin.dashboard, { days })
      .then(setD)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  }, [days]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, flex: 1 }}>Panel</h1>
        {[1, 7, 30, 90, 365].map((n) => (
          <button
            key={n}
            type="button"
            style={btn(days === n)}
            onClick={() => {
              setDays(n);
            }}
          >
            {n === 1 ? 'Hoy' : `${String(n)} días`}
          </button>
        ))}
      </div>
      <Notice text={error} error />
      {!d && !error && <p style={muted}>Cargando…</p>}
      {d && (
        <>
          <Tiles
            title="Pendiente del equipo"
            tiles={[
              {
                label: 'Soporte esperando respuesta',
                value: d.support.waiting_team,
                to: '/soporte?filtro=esperando',
                sub: 'el último mensaje es del usuario',
                alert: true,
              },
              {
                label: 'Chat con persona (escalados)',
                value: d.support.escalated_open,
                to: '/soporte?filtro=escalados',
                sub: 'el bot ya no atiende',
                alert: true,
              },
              {
                label: 'Pendientes de aceptar',
                value: d.orders.pending_acceptance,
                to: '/pedidos?status=paid_pending_acceptance',
              },
              {
                label: 'Incidencias abiertas',
                value: d.orders.disputes_open,
                to: '/incidencias',
                alert: true,
              },
              {
                label: 'Retiradas por aprobar',
                value: d.orders.withdrawals_requested,
                to: '/retiradas',
                alert: true,
              },
              {
                label: 'Denuncias abiertas',
                value: d.support.reports_open,
                to: '/reportes',
                alert: true,
              },
              {
                label: 'Anuncios en revisión',
                value: d.listings.pending_review,
                to: '/moderacion',
                alert: true,
              },
              {
                label: 'Cobros/reembolsos reintentándose',
                value: d.orders.money_pending,
                to: '/pedidos?filtro=dinero',
                sub: 'Stripe o wallet fallaron',
                alert: true,
              },
            ]}
          />
          <Tiles
            title={`Ingresos · ${days === 1 ? 'últimas 24 h' : `últimos ${String(days)} días`} (EUR)`}
            tiles={[
              ...revenueTiles(d.revenue.window),
              {
                label: 'PRO al mes (recurrente)',
                value: eur(d.users.pro_mrr),
                to: '/suscripciones',
                sub: 'suscripciones de pago vivas',
              },
            ]}
          />
          <Tiles
            title="Ingresos · desde el principio (EUR)"
            tiles={revenueTiles(d.revenue.all_time)}
          />
          <Tiles
            title="Usuarios"
            tiles={[
              { label: 'Usuarios', value: d.users.total, to: '/usuarios?filtro=todos' },
              {
                label: days === 1 ? 'Nuevos hoy' : `Nuevos (${String(days)} días)`,
                value: d.users.new_in_window,
                to: '/usuarios?filtro=nuevos',
              },
              { label: 'Usuarios PRO', value: d.users.pro, to: '/usuarios?filtro=pro' },
              ...d.users.pro_by_tier.map((t) => ({
                label: TIER_NAME[t.tier] ?? t.tier,
                value: t.count,
                to: `/usuarios?filtro=pro&nivel=${t.tier}`,
              })),
              {
                label: 'Profesionales pendientes',
                value: d.users.traders_pending,
                to: '/usuarios?filtro=profesionales',
              },
              { label: 'Baneados', value: d.users.banned, to: '/usuarios?filtro=baneados' },
              {
                label: 'Bloqueos entre usuarios',
                value: d.users.blocks,
                to: '/usuarios?filtro=bloqueos',
              },
            ]}
          />
          <Tiles
            title="Anuncios"
            tiles={[
              { label: 'Anuncios activos', value: d.listings.active, to: '/anuncios' },
              {
                label: 'Destacados pagados',
                value: d.listings.featured_paid,
                to: '/monetizacion',
                sub: 'vigentes, sin los incluidos en PRO',
              },
              ...d.listings.featured_paid_by_pack.map((p) => ({
                label: `Destacado · ${p.pack}`,
                value: p.count,
                to: '/monetizacion',
              })),
              {
                label: 'Destacados de PRO',
                value: d.listings.featured_pro,
                to: '/anuncios?filtro=destacados',
                sub: 'incluidos en el plan',
              },
              { label: 'Escaparates activos', value: d.listings.spotlights, to: '/monetizacion' },
            ]}
          />
          <Tiles
            title="Pedidos y actividad"
            tiles={[
              { label: 'Pedidos (24 h)', value: d.orders.last_24h, to: '/pedidos' },
              {
                label: `Pedidos (${String(days)} días)`,
                value: d.orders.in_window,
                to: '/pedidos',
              },
              { label: 'En envío', value: d.orders.in_shipping, to: '/pedidos?filtro=envio' },
              {
                label: 'Chats entre usuarios (24 h)',
                value: d.support.chats_24h,
                sub: 'conversaciones con mensajes',
              },
            ]}
          />
          <p style={muted}>
            Calculado {new Date(d.generated_at).toLocaleString('es-ES')}. Ingresos: pedidos
            completados; destacados al precio del paquete; importes en otras monedas convertidos con
            el tipo BCE del día.
          </p>
        </>
      )}
    </>
  );
}
